import { ScheduleItem } from '@/components/SchedulerTypes';
import { AffectedSchedule, Reassignment } from '@/components/ConflictResolutionModal';
import { formatDateToString, generateDateRange } from '@/lib/dateUtils';

/**
 * Detects schedules that will be affected when a staff member takes leave
 */
export const detectAffectedSchedules = (
  staffId: string,
  startDate: Date,
  endDate: Date | undefined,
  scheduleItems: ScheduleItem[],
  doctors: any[],
  rooms: any[]
): AffectedSchedule[] => {
  // Generate date range for the leave period
  const dateRange = generateDateRange(startDate, endDate);
  
  // Find all schedules where this staff member is assigned
  const affectedSchedules: AffectedSchedule[] = [];
  
  dateRange.forEach(dateStr => {
    const dailySchedules = scheduleItems.filter(item => 
      item.date === dateStr &&
      item.staffId === staffId &&
      item.type === 'appointment' && // Only affect appointment type schedules
      item.doctorId // Must have a doctor assigned
    );
    
    dailySchedules.forEach(schedule => {
      const doctor = doctors.find(d => d.id === schedule.doctorId);
      const room = rooms.find(r => r.id === schedule.roomId);
      
      affectedSchedules.push({
        scheduleId: schedule.id,
        date: schedule.date,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        doctorId: schedule.doctorId!,
        doctorName: doctor ? (doctor.short_name || doctor.name) : 'Unknown',
        roomId: schedule.roomId,
        roomName: room?.name,
        title: schedule.title,
        type: schedule.type
      });
    });
  });
  
  // Sort by date and time
  affectedSchedules.sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    if (dateCompare !== 0) return dateCompare;
    return a.startTime.localeCompare(b.startTime);
  });
  
  return affectedSchedules;
};

/**
 * Applies reassignments to affected schedules
 * Returns updated schedule items with new staff assignments
 */
export const applyReassignments = (
  scheduleItems: ScheduleItem[],
  reassignments: Reassignment[]
): ScheduleItem[] => {
  const updatedSchedules = [...scheduleItems];
  
  reassignments.forEach(reassignment => {
    const scheduleIndex = updatedSchedules.findIndex(
      item => item.id === reassignment.scheduleId
    );
    
    if (scheduleIndex !== -1) {
      const fromName = reassignment.originalStaffName || reassignment.originalStaffId;
      const toName = reassignment.newStaffName || reassignment.newStaffId;
      updatedSchedules[scheduleIndex] = {
        ...updatedSchedules[scheduleIndex],
        staffId: reassignment.newStaffId,
        notes: `${updatedSchedules[scheduleIndex].notes || ''}\nAuto-reassigned from ${fromName} to ${toName} (Preference #${reassignment.preferenceRank})`.trim()
      };
    }
  });
  
  return updatedSchedules;
};

/**
 * Validates if reassignments can be applied without conflicts
 */
export const validateReassignments = (
  reassignments: Reassignment[],
  scheduleItems: ScheduleItem[]
): { valid: boolean; conflicts: string[] } => {
  const conflicts: string[] = [];
  
  // Group reassignments by new staff and time
  const assignmentMap = new Map<string, Reassignment[]>();
  
  reassignments.forEach(reassignment => {
    const schedule = scheduleItems.find(s => s.id === reassignment.scheduleId);
    if (schedule) {
      const key = `${reassignment.newStaffId}-${schedule.date}-${schedule.startTime}`;
      if (!assignmentMap.has(key)) {
        assignmentMap.set(key, []);
      }
      assignmentMap.get(key)!.push(reassignment);
    }
  });
  
  // Check for double bookings
  assignmentMap.forEach((assignments, key) => {
    if (assignments.length > 1) {
      const [staffId, date, time] = key.split('-');
      conflicts.push(`Staff member cannot be assigned to multiple doctors at ${time} on ${date}`);
    }
  });
  
  return {
    valid: conflicts.length === 0,
    conflicts
  };
};

/**
 * Groups affected schedules by date for better visualization
 */
export const groupSchedulesByDate = (
  schedules: AffectedSchedule[]
): Map<string, AffectedSchedule[]> => {
  const grouped = new Map<string, AffectedSchedule[]>();
  
  schedules.forEach(schedule => {
    if (!grouped.has(schedule.date)) {
      grouped.set(schedule.date, []);
    }
    grouped.get(schedule.date)!.push(schedule);
  });
  
  return grouped;
};

/**
 * Calculates statistics for affected schedules
 */
export const getAffectedScheduleStats = (
  affectedSchedules: AffectedSchedule[]
): {
  totalAffected: number;
  uniqueDoctors: number;
  uniqueDates: number;
  byDoctor: Map<string, number>;
} => {
  const uniqueDoctors = new Set(affectedSchedules.map(s => s.doctorId));
  const uniqueDates = new Set(affectedSchedules.map(s => s.date));
  const byDoctor = new Map<string, number>();
  
  affectedSchedules.forEach(schedule => {
    const count = byDoctor.get(schedule.doctorId) || 0;
    byDoctor.set(schedule.doctorId, count + 1);
  });
  
  return {
    totalAffected: affectedSchedules.length,
    uniqueDoctors: uniqueDoctors.size,
    uniqueDates: uniqueDates.size,
    byDoctor
  };
};